import React from 'react';

function Privacy() {
  return (
    <div className="legal-page">
      <h1>Privacy Policy</h1>
      <p className="last-updated">Last updated: January 2025</p>

      <section>
        <h2>Overview</h2>
        <p>
          NZBEE is designed with privacy as a core principle. We believe your data belongs to you,
          and we've built our app to minimize data collection while maximizing functionality.
        </p>
      </section>

      <section>
        <h2>Legal Use and Copyright Notice</h2>
        <p>
          NZBEE is a technical tool that provides search and download management functionality.
          <strong> Users are solely responsible for ensuring they have the legal right to access and
          download any content.</strong> NZBEE does not host, store, or distribute any content. We do
          not endorse, encourage, or condone the downloading of copyrighted material without proper
          authorization.
        </p>
        <p>By using NZBEE, you agree to:</p>
        <ul>
          <li>Only search for and download content that you are legally authorized to access</li>
          <li>Comply with all applicable copyright laws and regulations in your jurisdiction</li>
          <li>Take full responsibility for your use of third-party services and content</li>
        </ul>
        <p><strong>NZBEE and its developers assume no liability for user actions or misuse of this application.</strong></p>
      </section>

      <section>
        <h2>Data Collection</h2>
        <p>
          NZBEE connects directly to your self-hosted download servers and any third-party search
          services you configure. We do not operate any intermediary servers that process or store
          your personal data.
        </p>

        <h3>Service Credentials</h3>
        <p>
          Your server URLs, API keys, and authentication credentials are stored locally on your device
          using iOS Keychain encryption. They are never transmitted to NZBEE servers. This includes:
        </p>
        <ul>
          <li>Third-party search service API keys</li>
          <li>Download server URLs, ports, API keys, usernames, and passwords</li>
          <li>All credentials remain on your device and are encrypted using iOS Keychain</li>
        </ul>

        <h3>iCloud Sync (Optional)</h3>
        <p>
          NZBEE offers optional iCloud sync that is <strong>disabled by default</strong>. When you
          enable iCloud sync in the app settings:
        </p>
        <ul>
          <li><strong>All server settings</strong> (host addresses, port numbers) are synced via iCloud Key-Value Storage</li>
          <li><strong>All credentials</strong> (API keys, usernames, passwords) are synced via iCloud Keychain</li>
        </ul>
        <p>
          This allows your configuration to be automatically available on all your devices signed in
          with the same Apple ID. All synced data is encrypted and protected by Apple's iCloud security.
          You can disable iCloud sync at any time in the app settings.
        </p>
        <p>
          <strong>Important:</strong> If you do not want your credentials synced to iCloud, keep iCloud
          sync disabled. When disabled, all data remains only on your local device.
        </p>

        <h3>Speech Recognition</h3>
        <p>
          If you use the voice search feature, NZBEE uses Apple's Speech Recognition framework to
          convert your voice input to text. This processing is handled by Apple's services according
          to Apple's privacy policy. The voice data is used only for the purpose of performing your
          search and is not stored or transmitted to NZBEE servers.
        </p>

        <h3>Usage Data</h3>
        <p>We do not collect analytics, usage statistics, or telemetry data about how you use the app.</p>

        <h3>Personal Information</h3>
        <p>We do not collect names, email addresses, or any personally identifiable information through the app.</p>
      </section>

      <section>
        <h2>Local Storage</h2>
        <p>
          All app data, including your service configurations, preferences, and cached search results,
          is stored locally on your device. This data is protected by iOS security mechanisms and is
          not accessible to other apps.
        </p>
      </section>

      <section>
        <h2>Third-Party Services</h2>

        <h3>Search Services</h3>
        <p>
          NZBEE allows you to configure third-party search service API connections. When you use search
          functionality, your search queries and API credentials are transmitted directly to the third-party
          service you have configured.
        </p>
        <p><strong>Important:</strong> NZBEE is not affiliated with, endorsed by, or responsible for any third-party search services. You are responsible for:</p>
        <ul>
          <li>Obtaining your own API access from third-party services</li>
          <li>Reviewing and agreeing to the third-party service's terms of use and privacy policy</li>
          <li>Ensuring your use of third-party services complies with their terms and applicable laws</li>
          <li>Understanding that third-party services may index content of varying legality</li>
        </ul>
        <p>
          We have no control over, and assume no responsibility for, the content, privacy policies,
          or practices of any third-party search services.
        </p>

        <h3>Self-Hosted Download Servers</h3>
        <p>
          NZBEE communicates directly with the self-hosted download servers you configure (SabNZBD, NZBGet).
          These communications occur between your device and your servers. We recommend using HTTPS to encrypt
          this traffic. NZBEE does not have access to, monitor, or store any information about the content you download.
        </p>
      </section>

      <section>
        <h2>iCloud Sync Detail</h2>
        <p>When iCloud sync is enabled:</p>
        <ul>
          <li><strong>Server Settings:</strong> Hostnames, port numbers, and enabled/disabled states are synced via Apple's iCloud Key-Value Storage</li>
          <li><strong>Credentials:</strong> All API keys, usernames, and passwords are synced via Apple's iCloud Keychain with end-to-end encryption</li>
          <li><strong>Access:</strong> All synced data is only accessible to devices signed in with your Apple ID</li>
          <li><strong>Control:</strong> You can enable or disable iCloud sync at any time in the app settings</li>
        </ul>
        <p>
          iCloud sync is <strong>OFF by default</strong> and only activates when you explicitly enable it in Settings.
          Apple manages all encryption and security for iCloud-synced data according to their security standards.
        </p>
      </section>

      <section>
        <h2>Data Deletion</h2>
        <p>You can delete all your data at any time:</p>

        <h3>To remove data from your device:</h3>
        <p>Uninstall the app - this removes all locally stored credentials, preferences, and cached data from iOS Keychain and local storage</p>

        <h3>To remove data from iCloud (if you enabled iCloud sync):</h3>
        <ol>
          <li>Disable iCloud sync in the app settings before uninstalling</li>
          <li>This removes synced settings from iCloud Key-Value Storage and stops syncing credentials</li>
          <li>To manually remove iCloud Keychain items, you can manage them through your device's Keychain settings</li>
        </ol>

        <p>
          <strong>Note:</strong> If you have iCloud sync enabled on multiple devices and uninstall from one device
          without first disabling sync, the settings will remain in iCloud and may appear if you reinstall the app
          on a device with the same Apple ID.
        </p>
      </section>

      <section>
        <h2>Children's Privacy</h2>
        <p>NZBEE is not directed at children under 13. We do not knowingly collect information from children under 13.</p>
      </section>

      <section>
        <h2>Changes to This Policy</h2>
        <p>
          We may update this privacy policy from time to time. We will notify you of any changes by posting
          the new policy on this page and updating the "Last updated" date.
        </p>
      </section>

      <section>
        <h2>Limitation of Liability</h2>
        <p>NZBEE is provided as a utility tool "as is" without warranties of any kind. The developers of NZBEE:</p>
        <ul>
          <li>Do not control, monitor, or have knowledge of what users search for or download</li>
          <li>Do not host, store, transmit, or distribute any content accessed through the app</li>
          <li>Are not responsible for how users choose to use the application</li>
          <li>Are not liable for any damages or legal issues arising from use or misuse of the application</li>
          <li>Make no representations about the legality of content accessible through third-party services</li>
        </ul>
      </section>

      <section>
        <h2>Contact</h2>
        <p>If you have questions about this privacy policy, please contact us at:</p>
        <p><a href="mailto:contact@nzbee.net">contact@nzbee.net</a></p>
      </section>

      <div className="disclaimer">
        <p>
          <strong>Disclaimer:</strong> NZBEE is an independent application and is not affiliated with,
          endorsed by, or associated with any third-party search services, download server software, or
          content providers. All third-party services and software are independent projects with their
          own terms, policies, and operators.
        </p>
      </div>
    </div>
  );
}

export default Privacy;
